import fs from 'node:fs/promises';

import type { LoreClient } from '../lib/api.ts';
import { log } from '../lib/logger.ts';
import { type ScrubThreadFileOptions, type ScrubbedThreadFile, scrubThreadFile } from '../scrubber/gate.ts';
import { quarantineThreadFile } from '../scrubber/quarantine.ts';
import { removeSnapshot } from './files.ts';
import type { LocalThreadFile } from './harnesses/types.ts';
import {
  type CompleteUploadSessionFileInfo,
  type UploadSessionFileInfo,
  completeUploadSession,
  createUploadSession,
  reorderLocalFilesByExpectedMD5,
  uploadSessionFile,
} from './uploadSession.ts';

// Best-effort byte size of a staged temp file, for diagnostic logging only.
// Returns null when the file can't be stat'd so a failure log never masks the
// original upload error with a stat error.
async function fileSizeBytes(tempPath: string): Promise<number | null> {
  try {
    return (await fs.stat(tempPath)).size;
  } catch {
    return null;
  }
}

// PUT each artifact's bytes to its presigned URL. Best-effort and parallel to
// the transcript: a single artifact failing is logged and skipped (the thread
// still uploads; that artifact just won't get a downloadable row) rather than
// failing the whole session.
async function uploadArtifactFiles(
  uploadFile: (presignedUrl: string, tempPath: string) => Promise<void>,
  sessionId: string,
  uploadFileInfo: UploadSessionFileInfo,
  file: ScrubbedThreadFile,
): Promise<void> {
  const presignedArtifacts = uploadFileInfo.artifact_files ?? [];
  if (presignedArtifacts.length === 0 || !file.artifactFiles?.length) return;

  for (const presigned of presignedArtifacts) {
    const presignedUrl = presigned.presigned_url?.trim();
    if (!presignedUrl) continue;
    const local = file.artifactFiles.find(
      (a) => a.kind === presigned.kind && a.fileName === presigned.file_name,
    );
    if (!local) continue;
    try {
      await uploadFile(presignedUrl, local.tempPath);
      log.info('upload_session_artifact_uploaded', {
        upload_session_id: sessionId,
        thread_file_id: uploadFileInfo.id,
        artifact_kind: presigned.kind,
        artifact_file_name: presigned.file_name,
      });
    } catch (error) {
      log.warn('upload_session_artifact_upload_failed', {
        upload_session_id: sessionId,
        thread_file_id: uploadFileInfo.id,
        artifact_kind: presigned.kind,
        artifact_file_name: presigned.file_name,
        // Artifact byte size is already known from the staged snapshot.
        file_size_bytes: local.sizeInBytes,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
}

/**
 * The single transcript-upload chokepoint. Every transcript/thread-file
 * upload (daemon, `lore export`, `lore share-codex`, `lore share-cowork`)
 * goes through here. It runs the client-side scrub gate (TAN-4341) on every
 * file BEFORE any upload: clean/redacted files upload (as `ScrubbedThreadFile`,
 * the only type `createUploadSession` accepts); quarantined files are held
 * locally and never uploaded. Then transport: create → presigned PUT →
 * complete. Callers interpret the returned per-file outcomes.
 */
export type ThreadFileOutcome =
  | {
      status: 'uploaded';
      localFile: LocalThreadFile;
      thread: CompleteUploadSessionFileInfo['thread'];
    }
  | {
      status: 'deduped';
      localFile: LocalThreadFile;
      thread: UploadSessionFileInfo['thread'];
    }
  | {
      status: 'no_upload';
      localFile: LocalThreadFile;
      error: string | null;
    }
  | {
      status: 'quarantined';
      localFile: LocalThreadFile;
      quarantineId: string;
      reason: string;
      findingCount: number;
    };

export type UploadThreadFilesOptions = {
  /** Injected for tests so the presigned PUT doesn't hit the network. */
  uploadFile?: (presignedUrl: string, tempPath: string) => Promise<void>;
  /** Injected for tests to control/skip the scrub gate. Defaults to the real gate. */
  scrub?: (localFile: LocalThreadFile, opts?: ScrubThreadFileOptions) => ReturnType<typeof scrubThreadFile>;
  /** Injected for tests; stamps quarantine timestamps. Defaults to now. */
  now?: Date;
  /** Bounds each create / presigned PUT / complete network call. */
  networkTimeoutMs?: number;
  /** Caller-owned upload metadata attached to every file in the session. */
  uploadMetadata?: Record<string, string>;
};

export type ScrubThreadFilesForUploadOptions = Pick<UploadThreadFilesOptions, 'scrub' | 'now'>;

export type ScrubThreadFilesForUploadResult = {
  outcomes: Array<ThreadFileOutcome | undefined>;
  passed: Array<{ file: ScrubbedThreadFile; inputIndex: number }>;
  redactedSnapshots: string[];
};

export async function scrubThreadFilesForUpload(
  localFiles: LocalThreadFile[],
  options: ScrubThreadFilesForUploadOptions = {},
): Promise<ScrubThreadFilesForUploadResult> {
  const scrub = options.scrub ?? scrubThreadFile;
  const now = options.now ?? new Date();

  const outcomes: Array<ThreadFileOutcome | undefined> = new Array(localFiles.length);
  // Files that passed the gate (clean or redacted), with their input index.
  const passed: Array<{ file: ScrubbedThreadFile; inputIndex: number }> = [];
  // Redacted snapshots we created and must clean up (the originals are the
  // callers' responsibility).
  const redactedSnapshots: string[] = [];

  // --- Scrub gate: runs on every file before any upload ---
  for (let i = 0; i < localFiles.length; i += 1) {
    const localFile = localFiles[i];
    if (!localFile) continue;
    const verdict = await scrub(localFile);
    if (verdict.status === 'quarantined') {
      const entry = await quarantineThreadFile({
        id: localFile.md5,
        tempPath: localFile.tempPath,
        sourcePath: localFile.sourcePath,
        harness: localFile.harness,
        harnessInternalId: localFile.harnessInternalId,
        filename: localFile.filename,
        findings: verdict.findings,
        now,
      });
      outcomes[i] = {
        status: 'quarantined',
        localFile,
        quarantineId: entry.id,
        reason: verdict.reason,
        findingCount: verdict.findings.length,
      };
      continue;
    }
    if (verdict.status === 'redacted') redactedSnapshots.push(verdict.file.tempPath);
    passed.push({ file: verdict.file, inputIndex: i });
  }

  return { outcomes, passed, redactedSnapshots };
}

export async function uploadThreadFiles(
  client: LoreClient,
  localFiles: LocalThreadFile[],
  options: UploadThreadFilesOptions = {},
): Promise<ThreadFileOutcome[]> {
  if (localFiles.length === 0) return [];

  const uploadFile =
    options.uploadFile ?? ((url, tempPath) => uploadSessionFile(url, tempPath, { timeoutMs: options.networkTimeoutMs }));
  const { outcomes, passed, redactedSnapshots } = await scrubThreadFilesForUpload(localFiles, options);

  try {
    if (passed.length === 0) return finalize(outcomes);

    const scrubbedFiles = passed.map((p) => p.file);
    const session = await createUploadSession(client, scrubbedFiles, {
      timeoutMs: options.networkTimeoutMs,
      uploadMetadata: options.uploadMetadata,
    });
    const uploadFiles = session.thread_files;
    if (uploadFiles.length !== scrubbedFiles.length) {
      throw new Error(
        `upload session returned ${uploadFiles.length} files for ${scrubbedFiles.length} local files`,
      );
    }

    // The server may reorder thread_files; reconcile to the files we sent.
    const filesInUploadOrder = reorderLocalFilesByExpectedMD5(scrubbedFiles, uploadFiles);
    const inputIndexByFile = new Map<ScrubbedThreadFile, number>();
    for (const entry of passed) {
      if (!inputIndexByFile.has(entry.file)) inputIndexByFile.set(entry.file, entry.inputIndex);
    }

    const uploadable: Array<{
      uploadFileInfo: UploadSessionFileInfo;
      file: ScrubbedThreadFile;
      inputIndex: number;
    }> = [];

    for (let i = 0; i < uploadFiles.length; i += 1) {
      const uploadFileInfo = uploadFiles[i];
      const file = filesInUploadOrder[i] as ScrubbedThreadFile | undefined;
      if (!uploadFileInfo || !file) continue;
      const inputIndex = inputIndexByFile.get(file);
      if (inputIndex === undefined) continue;

      log.info('upload_session_created', {
        upload_session_id: session.id,
        thread_file_id: uploadFileInfo.id,
        filename: file.filename,
        source_path: file.sourcePath,
        harness: file.harness,
        harness_internal_id: file.harnessInternalId,
        has_presigned_url: Boolean(uploadFileInfo.presigned_url?.trim()),
      });

      const presigned = uploadFileInfo.presigned_url?.trim();
      if (!presigned) {
        outcomes[inputIndex] = uploadFileInfo.thread?.id?.trim()
          ? { status: 'deduped', localFile: file, thread: uploadFileInfo.thread }
          : { status: 'no_upload', localFile: file, error: uploadFileInfo.error };
        continue;
      }
      uploadable.push({ uploadFileInfo, file, inputIndex });
    }

    for (const { uploadFileInfo, file } of uploadable) {
      try {
        await uploadFile(uploadFileInfo.presigned_url ?? '', file.tempPath);
        log.info('upload_session_file_uploaded', {
          upload_session_id: session.id,
          thread_file_id: uploadFileInfo.id,
          filename: file.filename,
          source_path: file.sourcePath,
          harness: file.harness,
          harness_internal_id: file.harnessInternalId,
        });
        await uploadArtifactFiles(uploadFile, session.id, uploadFileInfo, file);
      } catch (error) {
        log.error('upload_session_file_upload_failed', {
          upload_session_id: session.id,
          thread_file_id: uploadFileInfo.id,
          filename: file.filename,
          source_path: file.sourcePath,
          harness: file.harness,
          harness_internal_id: file.harnessInternalId,
          // Size of the bytes we actually attempted to PUT — the scrubbed
          // snapshot at tempPath, which can differ from the source after
          // redaction. Helps correlate upload timeouts with file size.
          file_size_bytes: await fileSizeBytes(file.tempPath),
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    }

    if (uploadable.length > 0) {
      let completed: Awaited<ReturnType<typeof completeUploadSession>>;
      try {
        completed = await completeUploadSession(
          client,
          session.id,
          uploadable.map((entry) => entry.uploadFileInfo),
          { timeoutMs: options.networkTimeoutMs },
        );
      } catch (error) {
        for (const { uploadFileInfo, file } of uploadable) {
          log.error('upload_session_complete_failed', {
            upload_session_id: session.id,
            thread_file_id: uploadFileInfo.id,
            filename: file.filename,
            source_path: file.sourcePath,
            harness: file.harness,
            harness_internal_id: file.harnessInternalId,
            // Size of the bytes that were PUT for this file (scrubbed snapshot
            // at tempPath); helps correlate completion failures with size.
            file_size_bytes: await fileSizeBytes(file.tempPath),
            error: error instanceof Error ? error.message : String(error),
          });
        }
        throw error;
      }
      if (completed.thread_files.length !== uploadable.length) {
        throw new Error(
          `completed upload session returned ${completed.thread_files.length} files for ${uploadable.length} local files`,
        );
      }
      for (let i = 0; i < uploadable.length; i += 1) {
        const entry = uploadable[i];
        const completedFile = completed.thread_files[i];
        if (!entry) continue;
        log.info('upload_session_completed', {
          upload_session_id: session.id,
          thread_file_id: entry.uploadFileInfo.id,
          filename: entry.file.filename,
          source_path: entry.file.sourcePath,
          harness: entry.file.harness,
          harness_internal_id: entry.file.harnessInternalId,
          thread_id: completedFile?.thread?.id?.trim() || '<none>',
        });
        outcomes[entry.inputIndex] = {
          status: 'uploaded',
          localFile: entry.file,
          thread: completedFile?.thread ?? null,
        };
      }
    }

    return finalize(outcomes);
  } finally {
    await Promise.all(redactedSnapshots.map((tempPath) => removeSnapshot(tempPath)));
  }
}

function finalize(outcomes: Array<ThreadFileOutcome | undefined>): ThreadFileOutcome[] {
  return outcomes.map((outcome, index) => {
    if (!outcome) {
      throw new Error(`upload outcome missing for thread file index ${index}`);
    }
    return outcome;
  });
}
